import { imageUrl } from "@/lib/api";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Trash2, BookOpen, Library, RotateCw, Loader2, AlertTriangle } from "lucide-react";

const STATUS_META = {
  done: { label: "Ready", cls: "text-emerald-400 border-emerald-400/40 bg-emerald-400/10" },
  error: { label: "Failed", cls: "text-red-400 border-red-400/40 bg-red-400/10" },
};
const inProgress = (s) => ["queued", "researching", "outlining", "writing"].includes(s);

export const RecentBooks = ({ open, onOpenChange, books, onOpen, onDelete, onRetry }) => {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-navy-950 border-gold-500/12 text-slate-100 w-full sm:max-w-md overflow-y-auto scroll-thin"
      >
        <SheetHeader>
          <SheetTitle className="font-display text-2xl text-slate-100 flex items-center gap-2">
            <Library className="w-5 h-5 text-slate-400" /> Your Library
          </SheetTitle>
          <SheetDescription className="font-ui text-sm text-slate-500">
            Books you've compiled with KLYR.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {books.length === 0 && (
            <div className="text-center py-16 text-slate-500 font-ui text-sm">
              <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-40" />
              No books yet. Write your first one!
            </div>
          )}
          {books.map((b) => {
            const running = inProgress(b.status);
            const failed = b.status === "error";
            const done = b.status === "done";
            const meta = STATUS_META[b.status];
            return (
              <div
                key={b.id}
                data-testid={`recent-book-card-${b.id}`}
                data-book-id={b.id}
                className="group flex gap-4 bg-navy-900/70 border border-gold-500/12 hover:border-gold-400/40 rounded-xl p-3 transition-colors"
              >
                <button data-testid={`recent-book-open-cover-${b.id}`} onClick={() => (done || running) && onOpen(b.id)} className="shrink-0" disabled={!done && !running}>
                  {b.cover_image_id ? (
                    <img
                      src={imageUrl(b.cover_image_id, 160)}
                      loading="lazy"
                      alt=""
                      className="w-16 h-20 object-cover rounded-md shadow"
                    />
                  ) : (
                    <div className="w-16 h-20 rounded-md bg-navy-800 flex items-center justify-center">
                      {running ? (
                        <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
                      ) : failed ? (
                        <AlertTriangle className="w-5 h-5 text-red-400" />
                      ) : (
                        <BookOpen className="w-6 h-6 text-slate-500" />
                      )}
                    </div>
                  )}
                </button>

                <div className="flex-1 min-w-0">
                  <button
                    data-testid={`recent-book-open-${b.id}`}
                    onClick={() => (done || running) && onOpen(b.id)}
                    disabled={!done && !running}
                    className="block text-left w-full"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`font-ui text-[10px] uppercase tracking-wider border rounded-full px-2 py-0.5 ${
                          meta ? meta.cls : "text-slate-400 border-gold-300/40 bg-gold-300/10"
                        }`}
                      >
                        {meta ? meta.label : b.queue_state === "waiting" ? "In line" : "Creating"}
                      </span>
                      {b.mode === "fiction" && (
                        <span className="font-ui text-[10px] uppercase tracking-wider text-slate-500">Novel</span>
                      )}
                    </div>
                    <h3 className="font-display text-lg font-semibold text-slate-100 leading-tight line-clamp-2 group-hover:text-gold-200 transition-colors">
                      {b.title || b.topic}
                    </h3>
                    {done ? (
                      <p className="font-ui text-xs text-slate-500 mt-1">
                        {["sudoku", "maze", "coloring", "wordsearch", "journal", "workbook", "children"].includes(b.mode)
                          ? `${b.chapters?.length || 0} pages`
                          : `${b.chapters?.length || 0} chapters · ${b.word_count?.toLocaleString() || 0} words`}
                      </p>
                    ) : (
                      <p className="font-ui text-xs text-slate-500 mt-1">
                        {running ? b.current_step || "Generating…" : "Generation failed"}
                      </p>
                    )}
                  </button>

                  {(failed || (!done && !running)) && (
                    <button
                      data-testid="recent-book-retry"
                      onClick={() => onRetry(b.id)}
                      className="mt-2 inline-flex items-center gap-1.5 font-ui text-xs text-slate-300 hover:text-gold-200 border border-gold-400/40 hover:border-gold-400 rounded-full px-3 py-1 transition-colors"
                    >
                      <RotateCw className="w-3.5 h-3.5" /> Retry
                    </button>
                  )}
                </div>

                <button
                  data-testid="recent-book-delete"
                  onClick={() => onDelete(b.id)}
                  className="shrink-0 self-start text-slate-500 hover:text-red-400 transition-colors p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
};
